/**
 * Versioned layout migration and validation (ADR-UI-001 decision 2).
 *
 * The legacy system's single most expensive omission was that saved layouts had
 * no schema version, so any node-id change silently broke every persisted user
 * layout. Here every layout carries `schemaVersion`; older versions are upgraded
 * through an explicit migration chain, and anything unknown, corrupt or from a
 * newer build falls back to the supplied role-default preset — never a blank or
 * broken workspace.
 */
import { clampSize } from "./geometry";
import type { PanelConstraints, PanelDescriptor, WorkspaceLayout } from "./types";

export const CURRENT_SCHEMA_VERSION = 2;

/**
 * Each entry upgrades a layout from version `N` to `N + 1`. Adding a panel
 * descriptor field ships a new step here; the chain is applied in order.
 */
export const migrations: Readonly<Record<number, (layout: Record<string, unknown>) => Record<string, unknown>>> = {
  // v1 (the shape an earlier alpha would have persisted): flat panel geometry,
  // no `fullscreen`, no max constraints, no `order`.
  1: (layout) => {
    const rawPanels = Array.isArray(layout["panels"]) ? layout["panels"] : [];
    const panels = rawPanels.map((panel, index) => {
      const p = isObject(panel) ? panel : {};
      return {
        id: p["id"],
        slot: p["slot"] ?? "main",
        position: { x: numberOr(p["x"], 0), y: numberOr(p["y"], 0) },
        size: { width: numberOr(p["width"], 320), height: numberOr(p["height"], 240) },
        constraints: {
          minWidth: numberOr(p["minWidth"], 160),
          minHeight: numberOr(p["minHeight"], 120),
        },
        collapsed: p["collapsed"] === true,
        fullscreen: false,
        order: numberOr(p["order"], index),
      };
    });
    return { schemaVersion: 2, presetId: layout["presetId"] ?? "migrated", panels };
  },
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function runMigrationChain(raw: unknown): Record<string, unknown> | null {
  if (!isObject(raw)) return null;
  const version = raw["schemaVersion"];
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) return null;
  // A layout from a newer build than this code cannot be safely down-migrated.
  if (version > CURRENT_SCHEMA_VERSION) return null;

  let current: Record<string, unknown> = raw;
  let at = version;
  while (at < CURRENT_SCHEMA_VERSION) {
    const step = migrations[at];
    if (step === undefined) return null;
    const next = step(current);
    if (!isObject(next)) return null;
    current = next;
    at += 1;
  }
  return current;
}

function validatePanel(raw: unknown): PanelDescriptor | null {
  if (!isObject(raw)) return null;
  const id = raw["id"];
  if (typeof id !== "string" || id === "") return null;

  const slot = typeof raw["slot"] === "string" && raw["slot"] !== "" ? raw["slot"] : "main";
  const position = isObject(raw["position"]) ? raw["position"] : {};
  const size = isObject(raw["size"]) ? raw["size"] : {};
  const constraintsRaw = isObject(raw["constraints"]) ? raw["constraints"] : {};

  const constraints: PanelConstraints = {
    minWidth: numberOr(constraintsRaw["minWidth"], 160),
    minHeight: numberOr(constraintsRaw["minHeight"], 120),
    ...(typeof constraintsRaw["maxWidth"] === "number" ? { maxWidth: constraintsRaw["maxWidth"] } : {}),
    ...(typeof constraintsRaw["maxHeight"] === "number" ? { maxHeight: constraintsRaw["maxHeight"] } : {}),
  };

  const clamped = clampSize(
    { width: numberOr(size["width"], constraints.minWidth), height: numberOr(size["height"], constraints.minHeight) },
    constraints,
  );

  return {
    id,
    slot,
    position: { x: numberOr(position["x"], 0), y: numberOr(position["y"], 0) },
    size: clamped,
    constraints,
    collapsed: raw["collapsed"] === true,
    fullscreen: raw["fullscreen"] === true,
    order: numberOr(raw["order"], 0),
  };
}

function validateLayout(
  raw: Record<string, unknown>,
  fallbackPresetId: string,
  knownPanelIds?: ReadonlySet<string>,
): WorkspaceLayout | null {
  if (raw["schemaVersion"] !== CURRENT_SCHEMA_VERSION) return null;
  if (!Array.isArray(raw["panels"])) return null;

  const presetId = typeof raw["presetId"] === "string" && raw["presetId"] !== "" ? raw["presetId"] : fallbackPresetId;
  const seen = new Set<string>();
  const panels: PanelDescriptor[] = [];

  for (const candidate of raw["panels"]) {
    const panel = validatePanel(candidate);
    if (panel === null) continue; // structurally-invalid panels are dropped safely
    if (seen.has(panel.id)) continue; // duplicate ids are dropped safely
    if (knownPanelIds !== undefined && !knownPanelIds.has(panel.id)) continue; // unknown ids dropped
    seen.add(panel.id);
    panels.push(panel);
  }

  return { schemaVersion: CURRENT_SCHEMA_VERSION, presetId, panels };
}

export interface MigrateOptions {
  /** When given, panels whose id is not in the set are dropped on load. */
  readonly knownPanelIds?: ReadonlySet<string>;
}

/**
 * Upgrades and validates an arbitrary persisted value into a current layout.
 * Returns `fallback` (the role default) for anything unknown, corrupt, from a
 * newer schema, or that yields zero valid panels.
 */
export function migrateLayout(
  raw: unknown,
  fallback: WorkspaceLayout,
  options?: MigrateOptions,
): WorkspaceLayout {
  try {
    const migrated = runMigrationChain(raw);
    if (migrated === null) return fallback;
    const validated = validateLayout(migrated, fallback.presetId, options?.knownPanelIds);
    if (validated === null || validated.panels.length === 0) return fallback;
    return validated;
  } catch {
    return fallback;
  }
}

/** Deep-plain copy, stripping any observability so it JSON-serialises cleanly. */
export function toPlainLayout(layout: WorkspaceLayout): WorkspaceLayout {
  return {
    schemaVersion: layout.schemaVersion,
    presetId: layout.presetId,
    panels: layout.panels.map((panel) => ({
      id: panel.id,
      slot: panel.slot,
      position: { x: panel.position.x, y: panel.position.y },
      size: { width: panel.size.width, height: panel.size.height },
      constraints: {
        minWidth: panel.constraints.minWidth,
        minHeight: panel.constraints.minHeight,
        ...(panel.constraints.maxWidth === undefined ? {} : { maxWidth: panel.constraints.maxWidth }),
        ...(panel.constraints.maxHeight === undefined ? {} : { maxHeight: panel.constraints.maxHeight }),
      },
      collapsed: panel.collapsed,
      fullscreen: panel.fullscreen,
      order: panel.order,
    })),
  };
}

export function serializeLayout(layout: WorkspaceLayout): string {
  return JSON.stringify(toPlainLayout(layout));
}

/** Parses a serialized layout string and migrates it; never throws. */
export function deserializeLayout(
  json: string,
  fallback: WorkspaceLayout,
  options?: MigrateOptions,
): WorkspaceLayout {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return fallback;
  }
  return migrateLayout(parsed, fallback, options);
}
